import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";

import type { UserAccountNotificationSettingsDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormSwitch } from "@/components/Form/FormSwitch";
import { PieChart01Icon } from "@/components/Icons/PieChart01Icon";
import { NotificationSettingsTable } from "@/components/NotificationSettingsTable/NotificationSettingsTable";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { selfAccountMutations } from "@/queries/self-account/mutations";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";
import { useTranslation } from "@/translations";

interface EditAccountNotificationSettingsFormValues {
  settings: UserAccountNotificationSettingsDto;
}

export function AccountNotificationSettingsPage(): React.ReactNode {
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const location = useLocation();
  const navigate = useNavigate();

  const selfAccountQueries = useSelfAccountQueries();
  const {
    data: selfAccountNotificationSettings,
    isPending: isPendingSelfAccountNotificationSettings,
    error: errorSelfAccountNotificationSettings,
  } = useQuery(selfAccountQueries.notificationSettings());
  const updateNotificationSettingsMutation = selfAccountMutations.useUpdateNotificationSettings();

  const formMethods = useForm<EditAccountNotificationSettingsFormValues>({
    defaultValues: {
      settings: {
        monthlyLivingExperienceReport: false,
      },
    },
    values: {
      settings: {
        monthlyLivingExperienceReport: selfAccountNotificationSettings?.monthlyLivingExperienceReport ?? false,
      },
    },
  });

  function handleSubmit(formValues: EditAccountNotificationSettingsFormValues) {
    updateNotificationSettingsMutation.mutate(
      {
        monthlyLivingExperienceReportEmail: formValues.settings.monthlyLivingExperienceReport,
      },
      {
        onSuccess: () => {
          showFlashToast({
            variant: "success",
            message: t("page.account-notification-settings.notifications.success"),
          });

          if (location.state?.fromPage) {
            void navigate(location.state.fromPage);
          }
        },
        onError: () => {
          showFlashToast({ variant: "alert", message: t("page.account-notification-settings.notifications.failed") });
        },
      },
    );
  }

  const isLoading = isPendingSelfAccountNotificationSettings;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const error = errorSelfAccountNotificationSettings;
  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.account-notification-settings.title")}
      contentSize="constrained"
      breadcrumbs={
        <Breadcrumbs
          pages={[
            { name: t("page.account.title"), to: "/account" },
            { name: t("page.account-notification-settings.title") },
          ]}
        />
      }
    >
      <Form.Root<EditAccountNotificationSettingsFormValues> formMethods={formMethods} onSubmit={handleSubmit}>
        <Stack gap="md">
          <SectionCard
            title={t("page.account-notification-settings.portfolio-reporting.title")}
            description={t("page.account-notification-settings.portfolio-reporting.description")}
            icon={<PieChart01Icon />}
          >
            <NotificationSettingsTable
              items={[
                {
                  label: t("page.account-notification-settings.portfolio-reporting.monthly-living-experience-report"),
                  renderOption: () => (
                    <FormSwitch
                      id="monthly-living-experience-report-switch"
                      name="settings.monthlyLivingExperienceReport"
                      size="sm"
                    />
                  ),
                },
              ]}
            />
          </SectionCard>
          <Form.Footer
            renderPrimaryAction={() => (
              <Button type="submit" isLoading={updateNotificationSettingsMutation.isPending}>
                {t("common.action.save")}
              </Button>
            )}
          />
        </Stack>
      </Form.Root>
    </DocumentPage>
  );
}
